import { useRef, useEffect } from 'react'
import { getChannelClient } from 'src/utils/messages/singleton'

// Shared by v6 (fiat/useCreditCard, fiat/useBank) and v7 (usePayment).
// When a third-party overlay modal appears inside the donate iframe —
// Stripe's 3DS challenge, or its Financial Connections bank-link modal
// for ACH — the iframe is often much smaller than the modal (especially
// the compact v7 widget), so the donor gets a cramped overlay with
// internal scrolling. This hook detects the overlay being injected and
// asks the host frame to full-screen the widget's wrapping <dialog> (via
// showModal) for the overlay's lifetime, then release it.
//
// Detection runs INSIDE the donate iframe (where the SDK injects the
// overlay); the takeover itself happens on the host page, driven by
// the `requestModalTakeover` / `releaseModalTakeover` channel messages
// that the embed script handles on the dialog it created (createWidget's
// Donate `wrap`). Only embedded widgets (`parent !== self`) participate
// — a standalone full-page widget already fills the viewport.
//
// Full-screening the iframe would visually yank the widget out of place,
// so the host sends back the rect the embedded iframe occupied (via
// `modalTakeoverLayout`, re-sent on resize/rotate). We inject CSS pinning
// the document body to that box with a transparent html/body, so the host
// page shows through the rest of the now-fullscreen iframe and the widget
// looks unmoved while the overlay covers the real viewport behind it. The
// injected CSS is removed on release.
//
// Signal: the SDK exposes no documented "modal shown" event, so we
// MutationObserver the document for a *viewport-covering* iframe from a
// recognized origin (Stripe by default; parametrized for reuse). Covering
// the viewport is the load-bearing discriminator — it excludes the
// always-present non-covering Stripe frames on the payment step (inline
// PaymentElement, the ACH search dropdown, the hidden 3DS-fingerprint /
// controller / metrics slivers) and survives the SDK renaming bundle URLs.
// Callers either bracket a known open/close window with startListening()
// / release() (3DS confirm, the v6 FC picker), or leave the observer
// running across a step and release on the overlay's removal (v7 FC).
//
// Failure modes are all graceful: if detection misses (unrecognized
// origin, never-covering), the modal shows in-widget as it does today;
// the safety timeout guarantees the page can't get stuck full-screen
// even if a caller forgets to release().

// Origins Stripe serves its overlay modals from (3DS challenge, Financial
// Connections). A sanity guard alongside the coversViewport check, not the
// primary signal — an unrecognized origin just means no takeover (graceful
// fallback). The default; the hook takes an `origins` option so a future
// non-Stripe modal (e.g. DonateStock-DAF) can supply its own. Update if
// monitoring shows Stripe drift.
const STRIPE_ORIGINS = [
  'https://js.stripe.com',
  'https://hooks.stripe.com',
]

// Belt to release()'s suspenders: if a confirm somehow never resolves
// (and release isn't called), auto-release so the host page can't sit
// frozen full-screen forever.
const SAFETY_TIMEOUT_MS = 120000

// How long to keep re-checking a candidate frame for coverage after we
// first see it. The overlay frame is frequently inserted (or has its src
// set) a handful of frames before it's sized/shown, so a single check
// races and intermittently misses. Non-covering frames (the hidden 3DS-
// fingerprint frame, the inline PaymentElement) never start covering, so
// polling them is harmless — it just costs a few no-op frames until the
// window lapses.
const DETECT_WINDOW_MS = 2000

const frameFrom = (node, origins) => {
  if (node.nodeType !== 1) return null
  const frames = node.tagName === 'IFRAME'
    ? [ node ]
    : [ ...(node.querySelectorAll?.('iframe') || []) ]
  return frames.find(f => origins.some(o => f.src?.startsWith(o))) || null
}

// The load-bearing discriminator: a modal worth taking over for covers
// (nearly) the whole iframe viewport. Separates a real overlay (FC modal,
// 3DS challenge) from the always-present non-covering Stripe frames on the
// payment step. Behavioral, so it survives the SDK renaming bundle URLs.
const coversViewport = el => {
  if (!el.isConnected) return false
  const style = getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') return false
  const { width, height } = el.getBoundingClientRect()
  return width >= window.innerWidth * 0.9 && height >= window.innerHeight * 0.9
}

// autoListen: start observing on mount and manage takeover/release
// automatically across the whole component lifetime (v7's whole-payment-
// step mode). Left off, the caller brackets a known window explicitly with
// startListening()/release() (v6's FC picker and card 3DS).
const useModalTakeover = ({ enabled = true, origins = STRIPE_ORIGINS, autoListen = false } = {}) => {
  const observerRef = useRef(null)
  const takenOverRef = useRef(false)
  const takenFrameRef = useRef(null)
  const safetyTimerRef = useRef(null)
  const layoutStyleRef = useRef(null)
  const bodyObserverRef = useRef(null)
  const pollsRef = useRef(null)

  // Only embedded widgets need a takeover; a top-frame standalone widget
  // already fills the viewport, so the challenge has room as-is.
  const embedded = typeof window !== 'undefined' && window.parent !== window.self

  // Report the height the widget renders at (it self-sizes to the width
  // the host pins it to) so the host can hold matching space on the page.
  const reportHeight = () =>
    getChannelClient().send('modalTakeoverHeight', { height: document.body.offsetHeight })

  // The host sends the box the embedded iframe occupied on the page
  // (re-sent on resize/rotate), plus the iframe's border-radius/box-shadow.
  // The iframe is now full-viewport, so we pin the document body back to
  // that box and make html/body transparent — the host page shows through
  // everywhere else, so the widget looks unmoved while Stripe's challenge
  // (position:fixed) covers the real viewport behind it. We re-create the
  // iframe's rounding/shadow on the body (overflow:hidden clips the square
  // content to the radius, exactly as the iframe edge did inline) — the
  // position:fixed challenge escapes the clip, so it still covers the
  // viewport. Height flows from content (the widget sizes to `width`
  // exactly as it did inline); a ResizeObserver reports that height back
  // so the host's reserved space tracks any reflow.
  const applyLayout = ({ top, left, width, borderRadius, boxShadow }) => {
    // modalTakeoverLayout is broadcast to every hook instance on the page,
    // but only the one that actually initiated the takeover should pin the
    // body. v6 mounts two instances (useCreditCard + useBank); without this
    // guard the non-initiating one also injects a pinning <style> that it
    // never releases (only the initiator calls release()), leaving the
    // widget stuck after the modal closes.
    if (!takenOverRef.current) return
    let style = layoutStyleRef.current
    if (!style) {
      style = document.createElement('style')
      document.head.appendChild(style)
      layoutStyleRef.current = style
    }
    style.textContent = `
      html { background: transparent !important; }
      body {
        position: fixed !important;
        margin: 0 !important;
        background: transparent !important;
        overflow: hidden !important;
        top: ${top}px !important;
        left: ${left}px !important;
        width: ${width}px !important;
        border-radius: ${borderRadius} !important;
        box-shadow: ${boxShadow} !important;
      }
    `
    if (!bodyObserverRef.current) {
      bodyObserverRef.current = new ResizeObserver(reportHeight)
      bodyObserverRef.current.observe(document.body)
    }
  }

  const clearLayout = () => {
    bodyObserverRef.current?.disconnect()
    bodyObserverRef.current = null
    layoutStyleRef.current?.remove()
    layoutStyleRef.current = null
  }

  // End the active takeover (drop full-screen) but KEEP the observer
  // running, so a later overlay in the same step — a 3DS challenge after
  // the FC picker, or a subscription's second challenge — re-triggers a
  // takeover. This is the removal-driven and safety-timeout path.
  const endTakeover = () => {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current)
      safetyTimerRef.current = null
    }
    takenFrameRef.current = null
    if (takenOverRef.current) {
      takenOverRef.current = false
      getChannelClient().send('releaseModalTakeover')
    }
    clearLayout()
  }

  // Full teardown: stop observing and end any active takeover. Called on
  // unmount, and by callers that bracket a known window (v6 FC picker,
  // v6 card 3DS).
  const release = () => {
    observerRef.current?.disconnect()
    observerRef.current = null
    pollsRef.current?.forEach(stop => stop())
    pollsRef.current = null
    endTakeover()
  }

  const takeover = frame => {
    if (takenOverRef.current) return
    takenOverRef.current = true
    takenFrameRef.current = frame
    getChannelClient().send('requestModalTakeover')
    // Belt to removal's suspenders: if the overlay never leaves the DOM
    // (and no removal fires), drop the takeover so the page can't sit
    // frozen full-screen forever — endTakeover, not release, so the
    // observer keeps working for the rest of the step.
    safetyTimerRef.current = setTimeout(endTakeover, SAFETY_TIMEOUT_MS)
  }

  const startListening = () => {
    if (!enabled || !embedded || observerRef.current) return
    const polls = new Set()

    // Re-check a candidate frame's coverage every frame until it covers
    // the viewport or the window lapses, then take over. Bias to
    // false-negative (skip unless confirmed covering): a missed takeover
    // just degrades to the in-widget modal, whereas a false one would
    // full-screen a frictionless payment. The non-covering frames (hidden
    // 3DS-fingerprint frame, inline PaymentElement) are filtered out here —
    // they never cover, so the window lapses on them.
    //
    // The `takenOverRef.current ||` short-circuit assumes overlays are
    // STRICTLY SEQUENTIAL — while one is taken over, a second candidate's
    // poll stops immediately without re-arming, so it'd be dropped. That's
    // fine because each overlay is a single appear→remove cycle (probes:
    // FC and each 3DS challenge, incl. a subscription's two, never overlap
    // — the first is removed before the next appears). If Stripe ever
    // showed two covering frames at once, the second wouldn't take over.
    const watch = frame => {
      const start = performance.now()
      let raf = 0
      const stop = () => { cancelAnimationFrame(raf); polls.delete(stop) }
      const tick = () => {
        if (takenOverRef.current || coversViewport(frame)) { takeover(frame); stop(); return }
        if (performance.now() - start > DETECT_WINDOW_MS) { stop(); return }
        raf = requestAnimationFrame(tick)
      }
      polls.add(stop)
      raf = requestAnimationFrame(tick)
    }

    // childList catches the frame being inserted; attributes(src) catches
    // the SDK setting the src on an already-inserted frame (either path
    // alone misses depending on insertion order).
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          const frame = frameFrom(mutation.target, origins)
          if (frame) watch(frame)
          continue
        }
        for (const node of mutation.addedNodes) {
          const frame = frameFrom(node, origins)
          if (frame) watch(frame)
        }
      }
      // Removal-driven release: once the taken-over overlay leaves the DOM
      // (FC picker closed, 3DS resolved) drop the takeover but keep
      // observing for the next overlay in this step. isConnected (rather
      // than removedNodes membership) also handles the overlay being torn
      // down inside a wrapper ancestor. Probes confirmed each overlay is a
      // single appear→remove cycle, so no debounce is needed.
      if (takenFrameRef.current && !takenFrameRef.current.isConnected)
        endTakeover()
    })
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [ 'src' ],
    })
    observerRef.current = observer
    pollsRef.current = polls
  }

  // Listen for the host's layout pushes for as long as the component is
  // mounted (onAll gives a clean unsubscribe, unlike the single-handler
  // on()). Tear down the observer / takeover on unmount too, so neither
  // outlives the component (e.g. if the donor navigates away mid-challenge).
  useEffect(() => {
    if (!embedded) return
    const off = getChannelClient().onAll('modalTakeoverLayout', applyLayout)
    // Whole-step mode: observe for the component's lifetime, auto-taking
    // over/releasing per overlay. startListening no-ops when !enabled.
    if (autoListen) startListening()
    return () => { off(); release() }
  }, [])

  return { startListening, release }
}

export default useModalTakeover
